import * as ko from 'knockout';

import { app } from '../app';
import * as dashboardApi from '../api/dashboard';
import * as dimensionMetasApi from '../api/dimension_metas';
import i18n from '../i18n';
import { Deferred } from '../utils/deferred';
import { confirmDialog } from './confirm_dialog';
import './include_columns_picker';
import {
  Choice,
  averageByChoices,
  averageByTooltip,
  formatChoices,
  layoutAxisChoices,
  headerLayoutChoices,
} from './overview_export_option_choices';

const template = require('raw-loader!../../templates/components/save_view_dialog.html').default;

interface SaveViewDialogParams {
  result: Deferred<dashboardApi.OverviewExportPreset | null>;
  options: dashboardApi.OverviewExportPresetOptions;
  siteAttributeMetas: dimensionMetasApi.AttributeMetaData[];
}

class SaveViewDialog {
  name = ko.observable('');
  isSaving = ko.observable(false);
  tooltipVisible = ko.observable(false);
  tooltipBottom = ko.observable(0);
  tooltipLeft = ko.observable(0);

  exportFormat: ko.Observable<dashboardApi.ExportFormat>;
  includeSiteId: ko.Observable<boolean>;
  includeSiteCoords: ko.Observable<boolean>;
  includeCustomerName: ko.Observable<boolean>;
  includeProtocol: ko.Observable<boolean>;
  includeOwner: ko.Observable<boolean>;
  includePhotoDescriptions: ko.Observable<boolean>;
  siteAttributeMetas: dimensionMetasApi.AttributeMetaData[];
  selectedSiteAttributeIds: ko.ObservableArray<number>;
  visitHandling: ko.Observable<dashboardApi.LayoutAxis>;
  dimensionHandling: ko.Observable<dashboardApi.LayoutAxis>;
  headerLayout: ko.Observable<dashboardApi.HeaderLayout>;
  averageBy: ko.Observable<dashboardApi.AverageBy>;

  formatOptions: Choice<dashboardApi.ExportFormat>[] = formatChoices;
  visitHandlingOptions: Choice<dashboardApi.LayoutAxis>[] = layoutAxisChoices;
  dimensionHandlingOptions: Choice<dashboardApi.LayoutAxis>[] = layoutAxisChoices;
  headerLayoutOptions: Choice<dashboardApi.HeaderLayout>[] = headerLayoutChoices;
  averageByOptions: Choice<dashboardApi.AverageBy>[] = averageByChoices;
  averageByTooltip = averageByTooltip;

  private result: Deferred<dashboardApi.OverviewExportPreset | null>;
  private filters: dashboardApi.SavedViewFilters | undefined;
  private showIconsFor: string[] | undefined;
  private pictureSize: dashboardApi.OverviewExportPresetOptions['picture_size'];
  private tooltipAnchorRect: DOMRect | null = null;
  private tooltipRoot: HTMLElement | null = null;
  private tooltipFrameHandle: number | null = null;

  constructor(params: SaveViewDialogParams) {
    this.result = params.result;
    const opts = params.options;
    this.exportFormat = ko.observable(opts.export_format ?? dashboardApi.ExportFormat.EXCEL);
    this.includeSiteId = ko.observable(!!opts.include_site_id);
    this.includeSiteCoords = ko.observable(!!opts.include_site_coords);
    this.includeCustomerName = ko.observable(!!opts.include_customer_name);
    this.includeProtocol = ko.observable(!!opts.include_protocol);
    this.includeOwner = ko.observable(!!opts.include_owner);
    this.includePhotoDescriptions = ko.observable(!!opts.include_photo_descriptions);
    this.siteAttributeMetas = params.siteAttributeMetas || [];
    this.selectedSiteAttributeIds = ko.observableArray<number>(
      (opts.include_site_attribute_ids || []).slice()
    );
    this.visitHandling = ko.observable(opts.visit_handling ?? dashboardApi.LayoutAxis.COLUMN);
    this.dimensionHandling = ko.observable(
      opts.dimension_handling ?? dashboardApi.LayoutAxis.COLUMN
    );
    this.headerLayout = ko.observable(opts.header_layout ?? dashboardApi.HeaderLayout.OWN_ROW);
    this.averageBy = ko.observable(opts.average_by ?? 'plot');
    this.filters = opts.filters;
    this.showIconsFor = opts.show_icons_for;
    this.pictureSize = opts.picture_size;
  }

  onSave = async () => {
    const name = this.name().trim();
    if (!name) {
      app.showFormSubmissionErrorModal(i18n.t('Please enter a name for the view.')(), {
        reload: false,
      });
      return;
    }
    this.isSaving(true);
    const options: dashboardApi.OverviewExportPresetOptions = {
      export_format: this.exportFormat(),
      include_site_id: this.includeSiteId(),
      include_site_coords: this.includeSiteCoords(),
      include_customer_name: this.includeCustomerName(),
      include_protocol: this.includeProtocol(),
      include_owner: this.includeOwner(),
      include_photo_descriptions: this.includePhotoDescriptions(),
      include_site_attribute_ids: this.selectedSiteAttributeIds().slice(),
      visit_handling: this.visitHandling(),
      dimension_handling: this.dimensionHandling(),
      header_layout: this.headerLayout(),
      average_by: this.averageBy(),
      filters: this.filters,
      show_icons_for: this.showIconsFor,
      picture_size: this.pictureSize,
    };
    try {
      const existingPreset = await this.findPresetByName(name);
      if (existingPreset && !(await this.confirmUpdate(existingPreset.name))) {
        this.isSaving(false);
        return;
      }
      const validation = existingPreset
        ? await dashboardApi.updateExportPresetWithValidation(existingPreset.id, name, options)
        : await dashboardApi.createExportPresetWithValidation(name, options);
      if (validation.isValid && validation.originalResponse) {
        this.result.resolve(validation.originalResponse);
        return;
      }
      app.showFormSubmissionErrorModal(this.extractErrorMessage(validation.errors), {
        reload: false,
      });
    } catch {
      app.showFormSubmissionErrorModal(
        i18n.t('Could not save the view. Please try again.')(),
        { reload: false }
      );
    } finally {
      this.isSaving(false);
    }
  };

  private findPresetByName = async (
    name: string
  ): Promise<dashboardApi.OverviewExportPreset | undefined> => {
    const presets = await dashboardApi.listExportPresets();
    return presets.find((preset) => preset.name === name);
  };

  private confirmUpdate = async (name: string): Promise<boolean> => {
    try {
      await confirmDialog(
        i18n.t('Update view')(),
        i18n.t(
          'A view named "{{name}}" already exists. Update it with the new options?',
          { name }
        )(),
        '',
        false,
        i18n.t('Update')()
      );
      return true;
    } catch {
      return false;
    }
  };

  private extractErrorMessage = (rawErrors: unknown): string => {
    const errors = (rawErrors || {}) as { __all__?: string[]; non_field_errors?: string[] };
    const globalErrors = [...(errors.__all__ || []), ...(errors.non_field_errors || [])];
    return globalErrors.length > 0
      ? globalErrors.join('. ')
      : i18n.t('Could not save the view. Please try again.')();
  };

  onCancel = () => {
    this.result.resolve(null);
  };

  showTooltip = (_data: unknown, event: Event) => {
    const iconElement = event.currentTarget as HTMLElement | null;
    if (!iconElement) return;
    this.tooltipAnchorRect = iconElement.getBoundingClientRect();
    this.tooltipRoot = iconElement.closest<HTMLElement>('.save-view-dialog');
    const gapAboveIcon = 8;
    this.tooltipBottom(window.innerHeight - this.tooltipAnchorRect.top + gapAboveIcon);
    this.tooltipLeft(-9999);
    this.tooltipVisible(true);
    this.tooltipFrameHandle = window.requestAnimationFrame(this.placeTooltip);
  };

  private placeTooltip = () => {
    this.tooltipFrameHandle = null;
    if (!this.tooltipVisible() || !this.tooltipAnchorRect || !this.tooltipRoot) return;
    const tooltipElement = this.tooltipRoot.querySelector<HTMLElement>('.portaled-tooltip');
    if (!tooltipElement) return;
    const halfWidth = tooltipElement.offsetWidth / 2;
    const anchorCenterX = this.tooltipAnchorRect.left + this.tooltipAnchorRect.width / 2;
    const clampedLeft = Math.min(
      window.innerWidth - halfWidth,
      Math.max(halfWidth, anchorCenterX)
    );
    this.tooltipLeft(clampedLeft);
  };

  hideTooltip = () => {
    if (this.tooltipFrameHandle !== null) {
      window.cancelAnimationFrame(this.tooltipFrameHandle);
      this.tooltipFrameHandle = null;
    }
    this.tooltipAnchorRect = null;
    this.tooltipRoot = null;
    this.tooltipVisible(false);
  };

  dispose() {
    this.hideTooltip();
  }
}

ko.components.register('save-view-dialog', {
  viewModel: SaveViewDialog,
  template,
});

export function openSaveViewDialog(
  options: dashboardApi.OverviewExportPresetOptions,
  siteAttributeMetas: dimensionMetasApi.AttributeMetaData[] = []
): Promise<dashboardApi.OverviewExportPreset | null> {
  return app.formsStackController.push({
    title: i18n.t('Save view')(),
    name: 'save-view-dialog',
    params: {
      result: new Deferred<dashboardApi.OverviewExportPreset | null>(),
      options,
      siteAttributeMetas,
    },
  });
}
